// Yoga Radstock - SEO-optimized location page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaRadstock = () => {
  usePageMeta({
    title: "Yoga Classes Radstock | Annelie Lily",
    description: "Yoga classes for Radstock, Somerset. Small group Hatha & Yin yoga in Frome, just 15 minutes from Radstock. Personal attention, all levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-radstock"
  });

  const locationSchema = generateLocationSchema({
    townName: "Radstock",
    description: "Yoga classes for Radstock at Annelie Lily Yoga in Frome. Small group classes and private sessions just 15 minutes from Radstock.",
    url: "https://annelielily.com/yoga-radstock"
  });

  const faqs = [
    {
      question: "How far is Frome from Radstock?",
      answer: "Just 15 minutes via the A362. It's one of the quickest journeys to my studio — Radstock and Frome are practically neighbours."
    },
    {
      question: "What if I've never done yoga before?",
      answer: "You're in good company. Many of my students started as complete beginners. I offer modifications for every pose, so you can work at your own level without feeling lost or left behind."
    },
    {
      question: "Can I just drop in, or do I need to book?",
      answer: "I ask that you book in advance so I know who's coming and can keep classes at the right size. Just get in touch and I'll let you know what's available."
    },
    {
      question: "What styles of yoga do you teach?",
      answer: "Mainly Hatha yoga (traditional practice with postures and breathwork) and Yin yoga (slower, deeper stretches). I also offer Aerial yoga and private sessions."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Radstock" currentSlug="areas/yoga-radstock" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Radstock</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Radstock</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Radstock to Frome is just 15 minutes — close enough for a weekly practice, 
              easy enough to fit around work and life. Small, personal yoga classes where 
              you're known by name.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />15 min from Radstock</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Radstock Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Radstock is one of the closest towns to my Frome studio — practically on the doorstep. 
              The A362 makes it a straightforward 15-minute journey, which means yoga can realistically 
              become part of your regular routine.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
              small by design, usually 6-8 people. This isn't about fitting as many people in as possible — 
              it's about creating space where I can actually help you with your practice.
            </p>
            <p>
              Whether you're drawn to the more active <Link to="/areas/yoga-radstock/hatha-yoga" className="text-gold hover:underline">Hatha yoga near Radstock</Link> or 
              the slower release of <Link to="/areas/yoga-radstock/yin-yoga" className="text-gold hover:underline">Yin yoga in Radstock</Link>, 
              you'll find a calm and supportive environment. <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">Beginners</Link> are 
              always welcome.
            </p>
            <p>
              The main yoga styles I teach are <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link> (active, strength-building) and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> (slower, deeply restorative).
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Radstock</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga Radstock", desc: "Traditional practice building strength, flexibility, and calm. Includes postures, breathing, and relaxation.", link: "/areas/yoga-radstock/hatha-yoga" },
              { title: "Yin Yoga Radstock", desc: "Slow, deep stretches held for several minutes. Excellent for releasing tension and quieting the mind.", link: "/areas/yoga-radstock/yin-yoga" },
              { title: "Private Sessions Radstock", desc: "One-to-one yoga designed entirely around your needs.", link: "/areas/yoga-radstock/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
            Questions from Radstock Students
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try a Class?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Trowbridge", link: "/areas/yoga-trowbridge" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Westbury", link: "/areas/yoga-westbury" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaRadstock;
